import { useRef, useState, useMemo } from 'react';
import classNames from 'classnames';

import {
  ChatExploration,
  ChatMessage,
  ChatMode,
  ChatUserMessage,
  Cell,
  CanvasState,
} from '@/explore/types';
import { Breakpoint, useScreenSize } from '@/lib/hooks';
import { ConversationExplorationContextProvider } from '@/explore/conversation/conversation-exploration-context';
import { ErrorBanner } from '@/components/banner';
import { measureCanvasNodes } from '@/explore/components/canvas/utils';
import { isUserMessage } from '@/core/cell';
import { getLastExplorationCellId } from '@/core/conversation';
import { setExplorationCanvas, setCanvasNodes } from '@/core/exploration';
import { useConversationRepositoryContext } from '@/explore/conversation/conversation-repository-context';
import { useTrackEvent } from '@/lib/analytics';
import { generateUUID, isNil } from '@/lib/utils';

import { useBuildAccountUrl } from '@/lib/accounts/context';

import { buildExplorationUrl } from '@/explore/utils';

import { useConversation } from './use-conversation';
import { ExplorationCell } from '../exploration-cell';
import { ChatMessages } from '../chat-cell/components/chat-messages';
import { UserMessageInput } from '../chat-cell/components/user-message-input';
import { ConversationProgressNew } from '../chat-cell/components/conversation-progress';
import { useExplorationUIContext } from '../exploration-ui-context';
import { useExplorationContext } from '../exploration-context';
import { useExplorationCellContext } from '../exploration-cell-context';

import styles from './conversation-cell.module.scss';
import notebookStyles from '../notebook/exploration-notebook.module.scss';

type MessageGroup =
  | {
      type: 'exploration';
      messages: ChatExploration[];
    }
  | {
      type: 'messages';
      messages: ChatMessage[];
    };

type ConversationCellViewProps = {
  conversationId: string;
};

export const ConversationCellView = (props: ConversationCellViewProps) => {
  const { conversationId } = props;
  const { error, refetch } = useConversation(conversationId);
  const {
    getConversation,
    addUserMessage,
    updateUserMessage,
    cancelConversationJob,
    rerunConversationJob,
    discardMessage,
    extractExplorationCells,
    extractExplorationCellsAfterMessage,
  } = useConversationRepositoryContext();
  const screenSize = useScreenSize();
  const buildAccountUrl = useBuildAccountUrl();
  const desktopView = screenSize.breakpoint > Breakpoint.md;
  const { exploration, removeConversation, acceptConversation } = useExplorationContext();
  const { endConversation } = useExplorationCellContext();
  const { selectedCell, openEditor, scrollToCell } = useExplorationUIContext();
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const [draft, setDraft] = useState<string | undefined>();
  const trackEvent = useTrackEvent();
  const [prevLastExplorationCellId, setPrevLastExplorationCellId] = useState<
    string | null | undefined
  >(undefined);

  const conversation = getConversation(conversationId);
  const canvasView = useMemo(
    () => exploration.view.options?.layout === 'canvas',
    [exploration.view.options],
  );

  const lastExplorationCellId = getLastExplorationCellId(conversation);
  if (lastExplorationCellId !== prevLastExplorationCellId) {
    setPrevLastExplorationCellId(lastExplorationCellId);
    // If a new exploration cell has been added, scroll to it
    if (prevLastExplorationCellId !== undefined && !isNil(lastExplorationCellId)) {
      scrollToCell(lastExplorationCellId);
    }
  }

  if (error !== undefined) {
    return (
      <ErrorBanner
        title={'Failed to load conversation'}
        actions={[
          { label: 'Retry', onClick: () => refetch() },
          {
            label: 'Remove conversation',
            onClick: () => removeConversation(conversationId),
          },
        ]}
      />
    );
  }

  if (conversation === undefined) {
    return null;
  }

  const clearDraft = () => {
    setDraft(undefined);
  };

  const updateCellSizes = (cells: Cell[]): CanvasState => {
    const persistedNodes = exploration.view.canvas?.nodes ?? [];

    if (!canvasView || !messagesRef.current) {
      return setCanvasNodes(exploration.view.canvas, persistedNodes);
    }

    const persistedNodeIds = persistedNodes.map((node) => node.id);
    const nodes = measureCanvasNodes(
      messagesRef.current,
      cells.map((cell) => cell.id),
    ).filter((node) => !persistedNodeIds.includes(node.id));
    return setCanvasNodes(exploration.view.canvas, persistedNodes.concat(nodes));
  };

  const handleDiscard = () => {
    const lastUserMessage = conversation.messages.findLast(isUserMessage);
    if (lastUserMessage !== undefined) {
      setDraft(lastUserMessage.message);
      discardMessage(conversationId, lastUserMessage.id);
      trackEvent('Conversation Discard Last', { conversationId });
    }
  };

  const handleDiscardAll = () => {
    removeConversation(conversationId);
    trackEvent('Conversation Discard All', { conversationId });
  };

  const handleAcceptLast = (event?: React.MouseEvent) => {
    event?.preventDefault();
    const lastUserMessage = conversation.messages.findLast(isUserMessage);
    if (lastUserMessage === undefined) {
      throw new Error('Cannot find blocks to accept');
    }
    const cells = extractExplorationCellsAfterMessage(conversationId, lastUserMessage.id);
    const canvasState = updateCellSizes(cells);
    acceptConversation(conversationId, cells, setExplorationCanvas(exploration, canvasState));
    trackEvent('Conversation Accept Last', { conversationId });
  };

  const handleAcceptAll = (event?: React.MouseEvent) => {
    event?.preventDefault();
    const cells = extractExplorationCells(conversationId);
    const canvasState = updateCellSizes(cells);
    acceptConversation(conversationId, cells, setExplorationCanvas(exploration, canvasState));
    trackEvent('Conversation Accept All', { conversationId });
  };

  const groupedMessages = conversation.messages.reduce<MessageGroup[]>((groups, message) => {
    const lastGroup = groups.at(-1);

    if (message.type === 'exploration') {
      if (lastGroup === undefined || lastGroup.type === 'messages') {
        groups.push({
          type: 'exploration',
          messages: [message],
        });
      } else if (lastGroup.type === 'exploration') {
        lastGroup.messages.push(message);
      }
    } else {
      if (lastGroup === undefined || lastGroup.type === 'exploration') {
        groups.push({
          type: 'messages',
          messages: [message],
        });
      } else if (lastGroup.type === 'messages') {
        lastGroup.messages.push(message);
      }
    }

    return groups;
  }, []);

  const handleAddUserMessage = async (content: string, sources: string[], mode: ChatMode) => {
    const correlationId = generateUUID();
    const updatedExploration = await addUserMessage(
      conversationId,
      content,
      sources,
      mode,
      correlationId,
    );

    if (updatedExploration !== null) {
      clearDraft();
      trackEvent('Conversation Add User Message', {
        message: content,
        conversationId,
        correlationId,
        originatingExplorationUrl: buildAccountUrl(buildExplorationUrl(exploration)),
        resultExplorationUrl: buildAccountUrl(buildExplorationUrl(updatedExploration)),
        isResearch: mode === 'research',
        sources,
        hasCurrentExploration: exploration.view.cells.length > 0,
      });
    }
  };

  const handleUpdateUserMessage = async (message: ChatUserMessage) => {
    const correlationId = generateUUID();
    const updatedExploration = await updateUserMessage(
      conversationId,
      message.id,
      message,
      correlationId,
    );

    if (updatedExploration !== null) {
      clearDraft();
      trackEvent('Conversation Update User Message', {
        message: message.message,
        conversationId,
        correlationId,
        originatingExplorationUrl: buildAccountUrl(buildExplorationUrl(exploration)),
        resultExplorationUrl: buildAccountUrl(buildExplorationUrl(updatedExploration)),
        isResearch: message.type === 'clarifying_answer' ? undefined : message.research,
        sources: message.type === 'clarifying_answer' ? undefined : message.sources,
        hasCurrentExploration: exploration.view.cells.length > 0,
      });
    }
  };

  const hasMessages = conversation.messages.length > 0;
  const isUserInputVisible =
    conversation.jobs.every((job) => job.status !== 'running') &&
    groupedMessages.at(-1)?.type !== 'messages';
  const lastUserMessageIdx = conversation.messages.findLastIndex(isUserMessage);
  const isInlineLoaderVisible =
    !isUserInputVisible && lastUserMessageIdx < conversation.messages.length - 1;
  const runningJobs = conversation.jobs.filter((job) => job.status === 'running');
  const latestJob = runningJobs.at(-1);

  const lastGroup = groupedMessages.at(-1);
  const lastMessage = lastGroup?.messages.at(-1);
  const allowDiscardingInitialMessage =
    lastGroup?.type === 'messages' &&
    lastGroup.messages.length === 1 &&
    lastMessage?.role === 'user' &&
    (latestJob === undefined || latestJob.status === 'idle');

  return (
    <ConversationExplorationContextProvider conversationId={conversationId}>
      {groupedMessages.length > 0 && (
        <div ref={messagesRef} className={styles.messages}>
          {groupedMessages.map((group, idx) => {
            switch (group.type) {
              case 'messages': {
                // Assuming the current UI only allows control over the last job in the message group
                // and all previous jobs are settled (no parallel jobs)
                const latestJobId = group.messages.findLast(
                  (message) => message.jobId !== undefined,
                )?.jobId;
                const latestJob = conversation.jobs.find((job) => job.jobId === latestJobId);

                return (
                  <div key={idx} className={styles.message}>
                    <ChatMessages
                      messages={group.messages}
                      editableInitialMessage={allowDiscardingInitialMessage}
                      status={
                        isInlineLoaderVisible && latestJob?.status === 'running'
                          ? 'idle'
                          : latestJob?.status === 'running'
                            ? 'loading'
                            : (latestJob?.status ?? 'idle')
                      }
                      statusMessage={latestJob?.statusMessage}
                      onAbort={() => {
                        if (latestJob === undefined) {
                          throw new Error('No job to abort');
                        }
                        const jobMessage = conversation.messages.find(
                          (message) => message.jobId === latestJob.jobId,
                        );
                        if (jobMessage !== undefined && isUserMessage(jobMessage)) {
                          setDraft(jobMessage.message);
                        }
                        cancelConversationJob(conversationId, latestJob.jobId);
                      }}
                      onAccept={(partial) =>
                        partial === true ? handleAcceptLast() : handleAcceptAll()
                      }
                      onDiscard={(partial) =>
                        partial === true ? handleDiscard() : removeConversation(conversationId)
                      }
                      onCancel={() =>
                        conversation.messages.filter(isUserMessage).length > 1
                          ? handleDiscard()
                          : removeConversation(conversationId)
                      }
                      onSubmit={handleAddUserMessage}
                      onMessageChange={handleUpdateUserMessage}
                      onRetry={() => {
                        if (latestJob === undefined) {
                          throw new Error('No job to retry');
                        }
                        rerunConversationJob(conversationId, latestJob.jobId);
                      }}
                    />
                  </div>
                );
              }
              case 'exploration':
                return (
                  <div
                    key={idx}
                    className={classNames({
                      [styles.responseGroup]: true,
                      [styles.borderlessGroup]: canvasView,
                      [styles.lastResponse]: idx === groupedMessages.length - 1,
                    })}>
                    {group.messages.map(({ exploration }) => (
                      <div key={exploration.explorationId} className={classNames(styles.message)}>
                        {exploration.view.cells.map((cell: any) => {
                          const isSelected = cell.id === selectedCell?.id;
                          return (
                            <ExplorationCell
                              cell={cell}
                              key={cell.id}
                              parameters={{}}
                              index={0}
                              selected={isSelected}
                              variables={[]}
                              isCollapsible={false}
                              isResizable={false}
                              canvasView={canvasView}
                              onSelectCell={() => !isSelected && desktopView && openEditor(cell.id)}
                            />
                          );
                        })}
                      </div>
                    ))}
                    {isInlineLoaderVisible && latestJob !== undefined && (
                      <ConversationProgressNew
                        statusMessage={latestJob.statusMessage ?? ''}
                        onAbort={() => cancelConversationJob(conversationId, latestJob.jobId)}
                      />
                    )}
                  </div>
                );
            }
          })}
        </div>
      )}
      {isUserInputVisible && (
        <div
          className={classNames(styles.messageInput, notebookStyles.conversationActive, {
            [notebookStyles.conversationLine]: hasMessages,
          })}>
          {hasMessages ? (
            <UserMessageInput
              placeholder="Follow up or request edits"
              initialValue={draft}
              onSubmit={handleAddUserMessage}
              onAccept={handleAcceptLast}
              primaryActions={(value, sources, mode) =>
                value.length > 0
                  ? [{ label: 'Ask', onClick: () => handleAddUserMessage(value, sources, mode) }]
                  : [
                      { label: 'Accept last', onClick: handleAcceptLast },
                      { label: 'Accept all blocks', onClick: handleAcceptAll },
                    ]
              }
              secondaryActions={[
                { label: 'Discard last', onClick: handleDiscard },
                {
                  label: 'Discard all blocks',
                  onClick: handleDiscardAll,
                },
              ]}
              autoFocus={!canvasView}
            />
          ) : (
            <UserMessageInput
              title="Question"
              placeholder="Type to ask AI a question"
              initialValue={draft}
              onSubmit={handleAddUserMessage}
              onCancel={endConversation}
              onAccept={handleAcceptLast}
              primaryActions={(value, sources, mode) => [
                {
                  label: 'Send',
                  disabled: value.length === 0,
                  onClick: () => handleAddUserMessage(value, sources, mode),
                },
              ]}
              secondaryActions={[{ label: 'Discard (Esc)', onClick: endConversation }]}
              autoFocus={!canvasView}
            />
          )}
        </div>
      )}
    </ConversationExplorationContextProvider>
  );
};
